import {useContext, useState} from "react";
import {ModalContext} from "../../../utils/modal/ModalProvider";
import {containsXss} from "../../../utils/security/XssFilter";
import {IAM_SERVER_SERVICE, IAM_SERVER_AUTH_TOKEN_POLICY, IAM_SERVER_SERVICE_PAGE} from "../../../utils/Config";
import axios from "../../../utils/api/axios";
import styles from './PageRegisterModal.module.css';
import { InnocodeButton, InnocodeInput, InnocodeDropdown, useDialog } from "@intsoft/innocode-core";


/**
 * 페이지 등록 모달 Container
 * @param update
 * @returns {JSX.Element}
 * @constructor
 */
export const PageRegisterModal = ({update, selectedServiceId, pageNameMap}) => {
    const {closeModal} = useContext(ModalContext);
    const { showDialog } = useDialog();
    const [isNull, setIsNull] = useState(false); //빈 값 확인
    const [isDuplicateChecked, setIsDuplicateChecked] = useState(false); // 중복 확인 완료 여부

    const [formData, setFormData] = useState({
        serviceId: selectedServiceId,
        pageName: '',
        pageUrl: '',
        parentPageId: ''
    });

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value
        }));
        if (name === 'pageUrl') {
            setIsDuplicateChecked(false); // pageUrl이 변경되면 중복 확인 다시 해야함
        }
    }

    // 서비스스 등록 요청 API
    const handleRegister = () => {
        for (let key in formData) {
            if (key !== 'parentPageId' && formData[key] === '') {
                setIsNull(true);
                showDialog({    
                    iconType: "warning",
                    type: 'error',
                    title: "경고",
                    message: "빈 칸을 입력해 주세요.",
                    confirmText: "확인",
                });
                return;
            } else if (containsXss(formData[key])) {
                setFormData((prevState) => ({
                    ...prevState,
                    [key]: ''
                })); 
                showDialog({
                    iconType: "warning",
                    type: 'error',
                    title: "경고",
                    message: "올바른 값을 입력해 주세요.",
                    confirmText: "확인",
                });
                return;
            }
        }

        if (!formData.pageUrl.startsWith('/')) {
            showDialog({
                iconType: "warning",
                type: 'error',
                title: "경고",
                message: "페이지 URL은 '/'로 시작해야 합니다.",
                confirmText: "확인",
            });
            return;
        }

        if (!isDuplicateChecked) {
            showDialog({
                iconType: "warning",
                type: 'error',
                title: "중복 확인",
                message: "페이지 URL 중복 확인을 해주세요.",
                confirmText: "확인",
            });
            return;
        }

        setIsNull(false);
        
        const pageParams = {
            serviceId: formData.serviceId,
            pageName: formData.pageName,
            pageUrl: formData.pageUrl,
            parentPageId:formData.parentPageId
        };
            

        const url = IAM_SERVER_SERVICE_PAGE + "/create";
        axios.post(url, pageParams)
            .then((res) => {
                showDialog({
                    iconType: "check",
                    type: 'info',
                    title: "성공",
                    message: res.data.message,
                    confirmText: "확인",
                    onConfirm: () => {
                        update();
                        closeModal();
                    },
                });
            })
            .catch((e) => {
                showDialog({
                    iconType: "warning",
                    type: 'error',
                    title: "실패",
                    message: e.response.data.resultMessage,
                    confirmText: "확인",
                });
            });
    }

    const handleDuplicateCheck = () => {
        if (!formData.serviceId) {
            showDialog({
                iconType: "warning",
                type: 'error',
                title: "중복확인",
                message: "서비스 url를 입력해주세요.",
                confirmText: "확인",
            });
            return;
        }

        const url = IAM_SERVER_SERVICE_PAGE + "/exists/url";

        const params = {
            serviceId: formData.serviceId,
            pageUrl: formData.pageUrl,
        };

        axios.post(url, params)
            .then(res => {
                if (res.data.data) {
                    setIsDuplicateChecked(false);
                    showDialog({
                        iconType: "warning",
                        type: 'error',
                        title: "중복확인",
                        message: "이미 사용 중인 url입니다.",
                        confirmText: "확인",
                    });
                } else {
                    setIsDuplicateChecked(true);
                    showDialog({
                        iconType: "check",
                        type: 'info',
                        title: "중복확인",
                        message: "사용 가능한 url입니다.",
                        confirmText: "확인",
                    });
                }
            })
            .catch(err => {
                console.error(err);
                showDialog({
                    iconType: "warning",
                    type: 'error',
                    title: "중복확인",
                    message: "오류가 발생했습니다.",
                    confirmText: "확인",
                });
            });
    };

    return (
        <div>
            <ul className={styles.layer__listWrap}>
                <li className={styles.layer__list}>
                    <p className={styles.layer__listTit}>페이지 명</p>
                    <InnocodeInput
                        // className={styles.layer__listInput}
                        type="text"
                        name="pageName"
                        value={formData.pageName}
                        onChange={(e) => handleChange(e)}
                        placeholder="페이지명을 입력하세요"
                    />
                </li>
                <li className={styles.layer__list}>
                    <p className={styles.layer__listTit}>페이지 URL</p>
                    <InnocodeInput
                        // className={styles.layer__listInput}
                        type="text"
                        name="pageUrl"
                        value={formData.pageUrl}
                        onChange={(e) => handleChange(e)}
                        placeholder="페이지 URL을 입력하세요 (예: /example)"
                    />
                    <InnocodeButton 
                        backgroundColor="var(--status-negative, #f83a3a)"
                        onClick={handleDuplicateCheck}>중복확인</InnocodeButton>
                </li>
                <li className={styles.layer__list}>
                    <p lang={"ko"} className={styles.layer__listTit}>상위 페이지</p>
                    <InnocodeDropdown 
                        placeholder="상위 페이지 선택"
                        value={formData.parentPageId || ''}
                        options={[
                            { value: '', label: ':: 상위 페이지 선택 ::', disabled: false },
                            ...(pageNameMap ? Object.entries(pageNameMap).map(([pageId, pageName]) => ({
                                value: pageId,
                                label: pageName,
                                disabled: false
                            })) : [])
                        ]}
                        onChange={(value) => {
                            handleChange({ target: { name: 'parentPageId', value: value || '' } });
                        }}
                    />
                </li>
            </ul>

            {/* {isNull ? <div className={`${styles.layer__text} ${styles.warning}`}>빈 칸을 확인해 주세요.</div> : null} */}
            {/* <button className={styles.layer__saveBtn} onClick={handleRegister}>등록</button> */}
            <div className={styles.layer__saveBtnBox}>  
                <InnocodeButton
                    size="lg"
                    designType="primary" 
                    fullWidth={true}
                    className={styles.layer__saveBtn}
                    onClick={handleRegister}
                >등록</InnocodeButton>
            </div>
        </div>
    )
}